import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { cloneDeep, uniq } from 'lodash';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import routeStatusOptions from '@/constants/routeStatusOptions';
import { useClientUser } from '@/hooks/useClientUser';
import { useBusinessTimezone } from '@/hooks/useBusinessTimezone';
import { toArrayLiteral } from '@/utilities/toArrayLiteral';
import { DISPATCH_PLAN_SERIALIZATION_KEY } from '@/graphql/serializingLink';
import { PLAN_PAGE_REMOVE_WITHOUT_UNLOCK } from '@/constants/featureFlags';
import { PlanningDataContext, PlanningSelectionContext, PlanningUiContext } from '../context';
import { ASSIGN_ORDERS_UPSERT, UNASSIGN_ORDERS_UPDATE } from '../graphql/mutations';
import { FETCH_ORDERS_BY_IDS, QUERY_PLANNING_DATA } from '../graphql/queries';
import { patchPlanningDataOnUnassign } from '../graphql/planningBuckets';
import { orderIsReassignable, rowRouteId } from '../util/orderIsReassignable';
import {
    LEG_ROUTE_TYPES,
    buildAssignMutations,
    buildOptimisticAssignResponse,
    buildOptimisticUnassignResponse,
    buildRouteInserts,
    isCustomerLeg,
    mappingTypeForOrder,
    patchPlanningData,
} from './assignToRouteHelpers';
import { preflightAssign } from '../util/preflightAssign';
import { useResourcesById } from '../hooks/useResourcesById';
import { toMinuteIso } from '@/utilities/toMinuteIso';

// Core assignment logic shared by AssignToRoute's manual dropdown flow and the
// auto-assign-on-select flow (useAutoAssignToRoute below). `assign` takes the
// target route and the selection map explicitly instead of reading component
// state, so the same code path — including every warning/notification and the
// optimistic cache patch — runs no matter how it's triggered.
//
// Both `assign` and `unassign` accept an optional `onSettled` callback, invoked
// exactly once when the operation is finished — after the server response lands
// (success or failure), or immediately on any pre-mutation guard return.
// useAutoAssignToRoute uses it to keep at most one of these full-replace
// mutations in flight; see the comment there for why that matters.
export const useAssignToRoute = () => {
    const { setSelectedOrders } = useContext(PlanningSelectionContext);
    const { setNotification, setError } = useContext(PlanningUiContext);
    const {
        state: { orders, allRoutes, variables: planningVariables },
        callbacks: { refetch },
    } = useContext(PlanningDataContext);
    const client = useApolloClient();
    const { user_id, circles, preferences_require_staging_verification } = useClientUser();
    const tz = useBusinessTimezone();
    const [assignOrdersUpsert, { loading: assignLoading }] = useMutation(ASSIGN_ORDERS_UPSERT);
    const [updateStops, { loading: unassignLoading }] = useMutation(UNASSIGN_ORDERS_UPDATE);

    const routesByKey = useMemo(() => {
        return Object.fromEntries(allRoutes.map((route) => [route.route_id, route]));
    }, [allRoutes]);

    const resourcesByKey = useResourcesById();

    const ordersByKey = useMemo(() => {
        return Object.fromEntries(orders.map((order) => [`${order.order_id}_${order.crossdock_leg}`, order]));
    }, [orders]);

    // Resolve a selection map ({ `${order_id}_${leg}`: bool }) to the order
    // rows that would actually be added to `targetRoute` — excludes orders
    // already on that route (either leg).
    const getOrdersToAdd = useCallback(
        (selectionMap, targetRoute) => {
            return Object.entries(selectionMap || {})
                .filter(([, selected]) => selected)
                .map(([key]) => ordersByKey[key])
                .filter(
                    (order) =>
                        order && !(order.pickup_route_id === targetRoute || order.dropoff_route_id === targetRoute)
                );
        },
        [ordersByKey]
    );

    // The inverse of getOrdersToAdd: the selected order-rows whose own leg is
    // already assigned to `targetRoute` — the candidates for removal.
    const getOrdersToRemove = useCallback(
        (selectionMap, targetRoute) => {
            return Object.entries(selectionMap || {})
                .filter(([, selected]) => selected)
                .map(([key]) => ordersByKey[key])
                .filter((order) => order && rowRouteId(order) === targetRoute);
        },
        [ordersByKey]
    );

    const assign = useCallback(
        (selectionMap, targetRoute, onSettled) => {
            // Fires exactly once, however this call ends.
            let hasSettled = false;
            const settle = () => {
                if (hasSettled) return;
                hasSettled = true;
                onSettled?.();
            };

            // Read from the Apollo cache rather than the closed-over routesByKey, which
            // is a React-context snapshot that only refreshes on re-render. The cache
            // reflects the optimistic patch of any assign that already landed, so this
            // sees the current stop sets for the destination route AND every route an
            // order is being moved off of. Correctness still depends on there being no
            // unresolved in-flight assign when this runs — useAutoAssignToRoute enforces
            // that; see the comment there.
            //
            // Merged over routesByKey rather than swapped for it: shell routes (client-
            // synthesized placeholders for resources with no persisted route yet, see
            // deriveShellRoutes) are never written to the Apollo cache, so a cache-only
            // lookup silently loses them the moment any real route also exists in the
            // cache (the common case) — assigning to a fresh resource would then no-op with
            // no error. routesByKey still has them; only cache-backed routes need the
            // fresher snapshot.
            const latestData = client.cache.readQuery({ query: QUERY_PLANNING_DATA, variables: planningVariables });
            const freshRoutesByKey = {
                ...routesByKey,
                ...(latestData?.routes ? Object.fromEntries(latestData.routes.map((r) => [r.route_id, r])) : {}),
            };

            const assignedRouteObj = freshRoutesByKey[targetRoute];
            const candidateOrders = getOrdersToAdd(selectionMap, targetRoute);
            if (!assignedRouteObj || candidateOrders.length === 0) {
                settle();
                return;
            }

            // Skip (and warn) for orders that are on a locked route. Same predicate
            // usePreviewOrders filters on, so the preview matches what gets saved.
            const ordersToAdd = candidateOrders.filter((order) => orderIsReassignable(order, freshRoutesByKey));
            if (ordersToAdd.length < candidateOrders.length) {
                setNotification({
                    severity: 'warning',
                    message:
                        'Cannot pull orders off a locked route. Unlock the route if necessary, make the desired change, and reoptimize.',
                });
            }
            if (ordersToAdd.length === 0) {
                settle();
                return;
            }

            // Both legs assigned to same route
            const dupeOrders = ordersToAdd.reduce((acc, order) => {
                return {
                    ...acc,
                    [order.order_id]: [...(acc[order.order_id] || []), order],
                };
            }, {});
            const alreadyAssigned = ordersToAdd.filter((order) =>
                assignedRouteObj?.orders?.some((mapping) => mapping.order_id === order.order_id)
            );
            if (alreadyAssigned.length > 0 || Object.values(dupeOrders).some((x) => x.length > 1)) {
                setNotification({
                    severity: 'warning',
                    message:
                        'Cannot assign both (pickup -> cross-dock) and (cross-dock -> delivery) legs of an order to the same route.',
                });
                settle();
                return;
            }

            // Invariant to ensure a locked route is not assigned to.
            if (assignedRouteObj?.status !== routeStatusOptions.PLANNING.value) {
                setNotification({
                    severity: 'warning',
                    message:
                        'Cannot assign to a locked route. If you would like to make edits, unlock the route if necessary, make the desired addition, and reoptimize the route.',
                });
                settle();
                return;
            }

            const preflight = preflightAssign({
                route: assignedRouteObj,
                resource: resourcesByKey[assignedRouteObj?.resource_id],
                ordersToAdd,
                tz,
            });
            if (preflight.notification) {
                setNotification(preflight.notification);
            }
            if (!preflight.proceed) {
                settle();
                return;
            }

            try {
                const newStops = FTLStopHelpers.addOrders(assignedRouteObj, ordersToAdd).map((stop) => ({
                    ...stop,
                    route_id: targetRoute,
                }));

                // Side-effectful: recompute stops on each previously-assigned route so
                // removed orders no longer appear on their old routes. Must run before
                // the mapping compute so freshRoutesByKey lookups still resolve correctly.
                let otherRouteStops = {};
                for (const order of ordersToAdd) {
                    const prevRouteKey =
                        order.crossdock_leg === 'pickup' ? order.pickup_route_id : order.dropoff_route_id;
                    const prevRoute = prevRouteKey && freshRoutesByKey[prevRouteKey];
                    if (prevRoute) {
                        const prevStops = otherRouteStops[prevRouteKey] || prevRoute.stopsByRouteId || [];
                        otherRouteStops[prevRouteKey] = FTLStopHelpers.removeStop(order, {
                            ...prevRoute,
                            stopsByRouteId: prevStops,
                        });
                    }
                }

                // Route assignment is an explicit `order_route_mappings` insert/delete
                // pair; no wh_event is emitted. See ./assignToRouteHelpers.js.
                const { mappingIdsToDelete, mappingsToDelete, mappingsToInsert } = buildAssignMutations({
                    ordersToAdd,
                    assignedRoute: targetRoute,
                    routesByKey: freshRoutesByKey,
                });

                const optimisticRoute = {
                    ...assignedRouteObj,
                    orders: [
                        ...(assignedRouteObj.orders || []),
                        ...ordersToAdd.map((o) => ({
                            order_id: o.order_id,
                            type: LEG_ROUTE_TYPES[o.crossdock_leg] || LEG_ROUTE_TYPES.default,
                            order: o,
                        })),
                    ],
                };

                const order_updates = ordersToAdd.map((order) => {
                    let customerOrderUpdate = {
                        del_window_start: null,
                        del_window_end: null,
                        original_del_window_start: null,
                        original_del_window_end: null,
                        delivery_time_confirmed: null,
                        confirmation_source: null,
                    };
                    const matchingStop = newStops.find(
                        (s) =>
                            (s.orders || []).includes(order.order_id) &&
                            FTLStopHelpers.isCustomerStop(s, optimisticRoute)
                    );
                    if (matchingStop && matchingStop.del_window_start) {
                        // Spread over the default so the confirmation reset survives:
                        // merging onto a stop that already has a window still means a
                        // window this order's customer never confirmed.
                        customerOrderUpdate = {
                            ...customerOrderUpdate,
                            del_window_start: toMinuteIso(matchingStop.del_window_start),
                            del_window_end: toMinuteIso(matchingStop.del_window_end),
                            original_del_window_start: toMinuteIso(matchingStop.del_window_start),
                            original_del_window_end: toMinuteIso(matchingStop.del_window_end),
                        };
                    }

                    return {
                        where: { order_id: { _eq: order.order_id } },
                        _set: {
                            ...(order.crossdock_leg === 'pickup'
                                ? {
                                      pickup_date: assignedRouteObj.scheduled_delivery,
                                  }
                                : {
                                      delivery_date: assignedRouteObj.scheduled_delivery,
                                      // Only the leg that sets delivery_date may claim the
                                      // route's resource.
                                      resource_id: assignedRouteObj.resource_id || null,
                                  }),
                            ...(isCustomerLeg(order) ? customerOrderUpdate : {}),
                            // Mirrors partnerAssignmentUpdates' contractor branch: carrier_id never changes,
                            // only contractor_id picks up the route's already-assigned partner.
                            ...(order.carrier_id === user_id && !!assignedRouteObj.carrier_id
                                ? { contractor_id: assignedRouteObj.carrier_id }
                                : {}),
                        },
                    };
                });

                // Single atomic call: Hasura resolves the root fields sequentially in
                // one transaction, and the document orders deletes before inserts, so
                // the previous routes' stale mappings/stops are gone before the new
                // ones land — the partial unique index on (order_id, type) where
                // duplicate_override='DISCARD' is never violated, and a failure
                // anywhere rolls back the whole assignment.
                const stopsByRoute = { ...otherRouteStops, [targetRoute]: newStops };
                const affectedRouteIds = Object.keys(stopsByRoute);

                const toStopInsert = ({ __typename, ...stop }) => {
                    return {
                        ...stop,
                        ...Object.fromEntries(
                            ['orders', 'returns', 'exchanges', 'actions_completed'].map((attr) => [
                                attr,
                                toArrayLiteral(uniq(stop[attr] || [])),
                            ])
                        ),
                    };
                };

                // Cleared optimistically below; restored on error so a failed assign
                // stays one click from retry.
                const selectionSnapshot = selectionMap;

                // False when the cached planning query was unreadable (e.g. another
                // flow evicted part of it) during the most recent update pass.
                let lastPatchApplied = false;

                // An assign changes every affected route's stop set — the
                // destination gains stops and each source route loses them — so
                // every stop after the edit shifts and the persisted del_window_*
                // values are stale. ASSIGN_ORDERS_UPSERT's on_conflict allows
                // exactly this one column, so it must carry a literal `true`:
                // spreading the cached route instead writes need_to_optimize back
                // over itself, leaving an already-optimized route unflagged (no
                // "Route modified, map and resubmit" chip, no submit guard) with
                // timeframes nothing will recalculate. Matches unassign below.
                const route_update = { need_to_optimize: true };

                const mutationPromise = assignOrdersUpsert({
                    // Serialized with the board's other full-replace mutations so
                    // rapid gestures cannot reorder on the wire.
                    context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
                    variables: {
                        route_ids: affectedRouteIds,
                        routes: buildRouteInserts({
                            affectedRouteIds,
                            routesByKey: freshRoutesByKey,
                            routeUpdate: route_update,
                            requireStagingVerification: preferences_require_staging_verification,
                        }),
                        order_updates,
                        stops: Object.values(stopsByRoute).flat().map(toStopInsert),
                        events: [],
                        mapping_ids_delete: mappingIdsToDelete,
                        mappings_insert: mappingsToInsert,
                    },
                    optimisticResponse: buildOptimisticAssignResponse({
                        affectedRoutes: affectedRouteIds.map((routeId) => freshRoutesByKey[routeId]),
                        stopsByRoute,
                        orderUpdates: order_updates,
                        ordersToAdd,
                        mappingsToInsert,
                        mappingsToDelete,
                        routeUpdate: route_update,
                    }),
                    // Runs for both the optimistic and the real response; paints the
                    // board from the mutation's returning data instead of a refetch.
                    update: (cache, { data }) => {
                        cache.updateQuery({ query: QUERY_PLANNING_DATA, variables: planningVariables }, (prev) => {
                            lastPatchApplied = !!prev?.routes;
                            return patchPlanningData(prev, { data, ordersToAdd, orderUpdates: order_updates });
                        });
                    },
                    onCompleted: (data) => {
                        // ASSIGN_ORDERS_UPSERT returns only { order_id } from
                        // update_orders_many to avoid the per-block OrderFields
                        // re-execution bottleneck. Fetch the updated orders in one
                        // bulk SELECT so the cache reflects server-confirmed values
                        // (computed columns, trigger effects) after the write.
                        const updatedOrderIds = (data?.update_orders_many || [])
                            .flatMap((res) => res?.returning || [])
                            .map((r) => r.order_id)
                            .filter(Boolean);
                        if (updatedOrderIds.length > 0) {
                            client
                                .query({
                                    query: FETCH_ORDERS_BY_IDS,
                                    variables: { order_ids: updatedOrderIds },
                                    fetchPolicy: 'network-only',
                                })
                                .catch(() => {});
                        }
                        // The server committed but the patch couldn't paint (the
                        // cached planning query was unreadable, e.g. another flow
                        // evicted part of it) — re-fetch rather than leave the
                        // assignment invisible. Dedup must be bypassed: an already
                        // in-flight planning refetch predates this assign, and
                        // attaching to it would paint the pre-assign snapshot.
                        if (!lastPatchApplied) {
                            client
                                .query({
                                    query: QUERY_PLANNING_DATA,
                                    variables: planningVariables,
                                    fetchPolicy: 'network-only',
                                    context: { queryDeduplication: false },
                                })
                                .catch(() => {});
                        }
                        settle();
                    },
                    onError: (error) => {
                        setSelectedOrders(selectionSnapshot);
                        setError(error, 'Error assigning orders to route');
                        settle();
                    },
                });
                // Backstop: a rejection that never reaches onError must still
                // release the queue, or every later assign would stall.
                mutationPromise?.catch?.(() => settle());

                setSelectedOrders({});
            } catch (error) {
                setError(error, error.message || 'Error assigning orders to route');
                settle();
            }
        },
        [
            routesByKey,
            resourcesByKey,
            getOrdersToAdd,
            user_id,
            preferences_require_staging_verification,
            assignOrdersUpsert,
            client,
            planningVariables,
            setSelectedOrders,
            setNotification,
            setError,
            tz,
        ]
    );

    // Remove the given orders' legs from `targetRoute` — the inverse of assign.
    // Reuses the board's canonical unassign cache path
    // (patchPlanningDataOnUnassign, same as StopRemoveModal): deletes each
    // order's DISCARD mapping for its leg, recomputes the route's stops, and
    // resets the freed orders' status/window scalars. Orders keep their
    // scheduled date — they return to that day's unassigned bucket rather than
    // the unscheduled pool. Uses the slim UNASSIGN_ORDERS_UPDATE doc (order_id-
    // only update_orders_many returning) plus a bulk FETCH_ORDERS_BY_IDS in
    // onCompleted, matching the ASSIGN_ORDERS_UPSERT optimization.
    const unassign = useCallback(
        (selectionMap, targetRoute, onSettled) => {
            let hasSettled = false;
            const settle = () => {
                if (hasSettled) return;
                hasSettled = true;
                onSettled?.();
            };

            // Same fresh-cache read as assign, merged the same way (see the comment
            // there re: shell routes never being in the Apollo cache): this also
            // full-replaces the route's stop set, so computing it from the stale
            // React-context snapshot would drop stops added by an assign that
            // hasn't re-rendered yet.
            const latestData = client.cache.readQuery({ query: QUERY_PLANNING_DATA, variables: planningVariables });
            const freshRoutesByKey = {
                ...routesByKey,
                ...(latestData?.routes ? Object.fromEntries(latestData.routes.map((r) => [r.route_id, r])) : {}),
            };

            const route = freshRoutesByKey[targetRoute];
            const ordersToRemove = getOrdersToRemove(selectionMap, targetRoute);
            if (!route || ordersToRemove.length === 0) {
                settle();
                return;
            }

            // A locked route can only be edited when the remove-without-unlock
            // circle is granted, in which case the removal also unlocks it (same
            // rule StopRemoveModal applies). Otherwise refuse, matching assign.
            let additionalRouteUpdates = {};
            if (route.status !== routeStatusOptions.PLANNING.value) {
                if (!circles?.[PLAN_PAGE_REMOVE_WITHOUT_UNLOCK]) {
                    setNotification({
                        severity: 'warning',
                        message:
                            'Cannot remove from a locked route. Unlock the route if necessary, make the desired change, and reoptimize the route.',
                    });
                    settle();
                    return;
                }
                additionalRouteUpdates = { status: 'planning' };
            }

            try {
                let newStops = cloneDeep(route.stopsByRouteId || []);
                for (const order of ordersToRemove) {
                    newStops = FTLStopHelpers.removeStop(order, { ...route, stopsByRouteId: newStops });
                }

                // Delete only the mapping for each order's specific leg (matched
                // by type), so removing one leg of a crossdock order never drops
                // the other leg's assignment.
                const legsToRemove = new Set(ordersToRemove.map((o) => `${o.order_id}_${mappingTypeForOrder(o)}`));
                const mappingIdsToDelete = (route.orders || [])
                    .filter((mapping) => mapping?.mapping_id && legsToRemove.has(`${mapping.order_id}_${mapping.type}`))
                    .map((mapping) => mapping.mapping_id);

                if (mappingIdsToDelete.length === 0) {
                    settle();
                    return;
                }

                // Reset the freed orders back to their pre-assignment state. The
                // scheduled date is intentionally preserved (delivery_date /
                // pickup_date untouched) so the order returns to the day's
                // unassigned bucket, not the unscheduled pool.
                //
                // Confirmation/window are order-level scalars owned by the
                // customer-facing leg, so only that leg clears them — same
                // isCustomerLeg rule assign applies. Removing a return's
                // warehouse leg must leave its still-routed pickup leg's
                // confirmation alone.
                const order_updates = ordersToRemove.map((order) => {
                    return {
                        where: { order_id: { _eq: order.order_id } },
                        _set: {
                            order_status: order.carrier_id ? 'claimed' : 'pending',
                            // Mirrors partnerAssignmentUpdates' unassign branch: releasing the partner just
                            // clears contractor_id, carrier_id is never touched.
                            ...(order.contractor_id ? { contractor_id: null } : {}),
                            // The leg that claimed the resource on assign is the leg that releases
                            // it, so freed orders stop counting against its capacity.
                            ...(order.crossdock_leg !== 'pickup' ? { resource_id: null } : {}),
                            ...(isCustomerLeg(order)
                                ? {
                                      delivery_time_confirmed: null,
                                      confirmation_source: null,
                                      del_window_start: null,
                                      del_window_end: null,
                                      original_del_window_start: null,
                                      original_del_window_end: null,
                                  }
                                : {}),
                        },
                    };
                });

                const selectionSnapshot = selectionMap;
                let needsRefetch = false;

                const route_update = { need_to_optimize: true, ...additionalRouteUpdates };
                const stopUpdates = FTLStopHelpers.gqlStopUpdates(newStops, route);
                // Full cached objects for the entities the optimistic response
                // must echo fragment-complete: the stops gqlStopUpdates drops
                // (by stop_id) and the mappings being deleted (by mapping_id).
                const deletedStopIds = new Set(stopUpdates.deletes || []);
                const deletedStops = (route.stopsByRouteId || []).filter((stop) => deletedStopIds.has(stop.stop_id));
                const deletedMappingIdSet = new Set(mappingIdsToDelete);
                const deletedMappings = (route.orders || []).filter((mapping) =>
                    deletedMappingIdSet.has(mapping.mapping_id)
                );

                const mutationPromise = updateStops({
                    // Serialized with assign and the other full-replace mutations
                    // so a remove can't reorder past an in-flight assign.
                    context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
                    variables: {
                        route_id: targetRoute,
                        route_update,
                        events: [],
                        mapping_ids_delete: mappingIdsToDelete,
                        order_updates,
                        ...stopUpdates,
                    },
                    // Paints the removal before the server round trip; the same
                    // patchPlanningDataOnUnassign runs on this pass and returns
                    // the freed order to its bucket, so the board never flickers
                    // the order off-route-and-nowhere.
                    optimisticResponse: buildOptimisticUnassignResponse({
                        route,
                        newStops,
                        deletedStops,
                        deletedMappings,
                        freedOrders: ordersToRemove,
                        orderUpdates: order_updates,
                        routeUpdate: route_update,
                    }),
                    update: (cache, { data }) => {
                        needsRefetch = patchPlanningDataOnUnassign(cache, {
                            data,
                            variables: planningVariables,
                        });
                    },
                    onCompleted: () => {
                        // patchPlanningDataOnUnassign flags a refetch when it
                        // can't prove the freed orders belong back in a bucket
                        // from the cache alone — that whole-board fetch already
                        // brings fresh order data, so skip the bulk fetch.
                        if (needsRefetch) {
                            refetch();
                            settle();
                            return;
                        }
                        // update_orders_many returns OrderDispatchScalars — every
                        // column this doc's _set writes, already normalized. The
                        // rest of OrderFields (computed columns, trigger effects
                        // outside that set) is still decoupled, so fetch the
                        // freed orders in one bulk SELECT.
                        const updatedOrderIds = ordersToRemove.map((order) => order.order_id).filter(Boolean);
                        if (updatedOrderIds.length > 0) {
                            client
                                .query({
                                    query: FETCH_ORDERS_BY_IDS,
                                    variables: { order_ids: updatedOrderIds },
                                    fetchPolicy: 'network-only',
                                })
                                .catch(() => {});
                        }
                        settle();
                    },
                    onError: (error) => {
                        setSelectedOrders(selectionSnapshot);
                        setError(error, 'Error removing orders from route');
                        settle();
                    },
                });
                mutationPromise?.catch?.(() => settle());

                setSelectedOrders({});
            } catch (error) {
                setError(error, error.message || 'Error removing orders from route');
                settle();
            }
        },
        [
            routesByKey,
            getOrdersToRemove,
            circles,
            user_id,
            updateStops,
            client,
            planningVariables,
            refetch,
            setSelectedOrders,
            setNotification,
            setError,
        ]
    );

    return { assign, unassign, getOrdersToAdd, getOrdersToRemove, assignLoading, unassignLoading };
};

// Auto-assign / auto-remove on select: when a route is already selected,
// selecting orders (via a list click, a map marker, or the lasso) runs them
// straight through the assignment logic — skipping the dropdown + Assign
// button. Only orders selected *after* a route is active trigger this, so
// clicking a route to inspect it never surprise-assigns previously selected
// orders.
//
// Toggle semantics for both a single click and a lasso: if the newly-selected
// set contains anything NOT yet on the route it's an assign (adds the unrouted
// orders; any already-routed orders in a mixed lasso are left untouched); only
// when the whole newly-selected set is already on the route do we remove it.
// assign and unassign are mutually exclusive per pass on purpose — each one
// replaces the route's stop set from the same pre-action snapshot, so firing
// both for one route would let their writes race and clobber each other.
export const useAutoAssignToRoute = () => {
    const { selectedRoute, selectedOrders } = useContext(PlanningSelectionContext);
    const { assign, unassign, getOrdersToAdd } = useAssignToRoute();

    // Keys selected as of the last effect pass, so we can act only on the ones
    // newly added this pass.
    const prevSelectedRef = useRef({});

    // ASSIGN_ORDERS_UPSERT and UNASSIGN_ORDERS_UPDATE are full-replace writes:
    // each one deletes every stop on the routes it touches and re-inserts a set
    // it computed client-side. That makes the last write win outright, so it is
    // only correct if the snapshot it computed from is current. Letting several
    // overlap means later ones must read the accumulated optimistic state of the
    // earlier ones out of the Apollo cache, and under real rapid-click timing
    // that read comes back stale — the persisted result is then one mutation's
    // partial snapshot, with orders holding a mapping to a route but no stop on
    // it (and orphaned stops left behind on the route they moved off).
    //
    // So: keep exactly one of these in flight. Clicks are queued immediately —
    // the first fires right away, and everything clicked while it round-trips is
    // coalesced into one follow-up that computes from the settled cache.
    // Rapid-clicking nine pins becomes two or three correct mutations instead of
    // nine racing ones, and no click is dropped.
    //
    // The queue is a FIFO of not-yet-sent batches, each `{ route, mode, orders }`.
    // Consecutive clicks merge into the tail batch when they target the same
    // route in the same direction; a mid-burst switch pushes a new batch rather
    // than discarding what was already queued.
    const queueRef = useRef([]);
    const inFlightRef = useRef(false);

    // Always invoke the latest callbacks — a queued flush outlives the render
    // whose closure enqueued it.
    const assignRef = useRef(assign);
    assignRef.current = assign;
    const unassignRef = useRef(unassign);
    unassignRef.current = unassign;

    const flush = useCallback(() => {
        if (inFlightRef.current) return;

        const batch = queueRef.current.shift();
        if (!batch) return;

        inFlightRef.current = true;

        const onSettled = () => {
            inFlightRef.current = false;
            // Drain whatever was clicked while this one was in flight.
            flushRef.current();
        };

        if (batch.mode === 'unassign') {
            unassignRef.current(batch.orders, batch.route, onSettled);
        } else {
            assignRef.current(batch.orders, batch.route, onSettled);
        }
    }, []);
    const flushRef = useRef(flush);
    flushRef.current = flush;

    useEffect(() => {
        const selectedNow = Object.entries(selectedOrders || {})
            .filter(([, selected]) => selected)
            .map(([key]) => key);

        // No active route → drop anything queued but not yet sent, and just
        // track selection so that orders selected before the route was picked
        // aren't treated as "new" once it is. An already in-flight call still
        // completes; its onSettled simply finds nothing left to drain.
        if (!selectedRoute) {
            queueRef.current = [];
            prevSelectedRef.current = { ...selectedOrders };
            return;
        }

        const newlySelected = selectedNow.filter((key) => !prevSelectedRef.current[key]);
        prevSelectedRef.current = { ...selectedOrders };

        if (newlySelected.length === 0) return;

        const newlyMap = Object.fromEntries(newlySelected.map((key) => [key, true]));

        // Anything to add → assign (adds the not-yet-routed orders; assign()
        // filters out anything already on the route). Nothing to add means the
        // whole newly-selected set is already on the route → remove it. This
        // covers the single-click toggle and multi-order lasso removal alike.
        const mode = getOrdersToAdd(newlyMap, selectedRoute).length > 0 ? 'assign' : 'unassign';

        const tail = queueRef.current[queueRef.current.length - 1];
        if (tail && tail.route === selectedRoute && tail.mode === mode) {
            tail.orders = { ...tail.orders, ...newlyMap };
        } else {
            queueRef.current.push({ route: selectedRoute, mode, orders: newlyMap });
        }

        flushRef.current();
    }, [selectedOrders, selectedRoute, assign, unassign, getOrdersToAdd]);
};
